import axios from "axios";
import moment from "moment";

/** Access levels on a cabinet, lowest first. */
export const ACCESS = {
  viewer: {
    label: "Viewer",
    short: "Can view",
    hint: "Browse, preview and download.",
    icon: "bi-eye",
  },
  contributor: {
    label: "Contributor",
    short: "Can upload",
    hint: "Also upload, add folders, and change what they added.",
    icon: "bi-upload",
  },
  manager: {
    label: "Manager",
    short: "Manager",
    hint: "Everything, including access, the Trash, and opening or closing uploads.",
    icon: "bi-shield-check",
  },
};
export const ACCESS_ORDER = ["viewer", "contributor", "manager"];

const TYPES = [
  { exts: ["pdf"], icon: "bi-file-earmark-pdf", tone: "pdf", label: "PDF" },
  { exts: ["doc", "docx", "odt", "rtf"], icon: "bi-file-earmark-word", tone: "doc", label: "Document" },
  { exts: ["xls", "xlsx", "ods", "csv"], icon: "bi-file-earmark-excel", tone: "sheet", label: "Spreadsheet" },
  { exts: ["ppt", "pptx", "odp"], icon: "bi-file-earmark-slides", tone: "slides", label: "Presentation" },
  {
    exts: ["jpg", "jpeg", "png", "gif", "webp", "bmp", "tif", "tiff"],
    icon: "bi-file-earmark-image",
    tone: "image",
    label: "Image",
  },
  { exts: ["zip", "rar", "7z"], icon: "bi-file-earmark-zip", tone: "zip", label: "Compressed" },
  { exts: ["mp3"], icon: "bi-file-earmark-music", tone: "media", label: "Audio" },
  { exts: ["mp4", "mov"], icon: "bi-file-earmark-play", tone: "media", label: "Video" },
  { exts: ["txt"], icon: "bi-file-earmark-text", tone: "text", label: "Text" },
];
const OTHER_TYPE = { icon: "bi-file-earmark", tone: "other", label: "File" };

export function fileType(extension) {
  const ext = String(extension || "").toLowerCase();
  return TYPES.find((t) => t.exts.includes(ext)) || OTHER_TYPE;
}

// Opened in the preview; other files are downloaded.
const PREVIEW_MIMES = ["application/pdf", "image/png", "image/jpeg", "image/gif", "image/webp", "image/bmp", "text/plain"];

export function canPreview(file) {
  return !!file && PREVIEW_MIMES.includes(file.mime);
}

export function formatSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = n / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value >= 10 ? Math.round(value) : value.toFixed(1).replace(/\.0$/, "")} ${units[unit]}`;
}

/** The largest upload, like "200 MB" or "2 GB". */
export function maxSizeText(limits) {
  const mb = Number(limits && limits.max_file_mb) || 200;
  return mb >= 1024 ? `${+(mb / 1024).toFixed(1)} GB` : `${mb} MB`;
}

/** "Documents, spreadsheets and images" from the allowed type groups. */
export function typesSentence(limits) {
  const labels = ((limits && limits.groups) || []).map((g, i) => (i === 0 ? g.label : g.label.toLowerCase()));
  if (labels.length < 2) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/** A short version for tight spots: "Documents, spreadsheets, presentations and more". */
export function typesShort(limits) {
  const groups = (limits && limits.groups) || [];
  if (groups.length <= 3) return typesSentence(limits);
  return `${groups
    .slice(0, 3)
    .map((g, i) => (i === 0 ? g.label : g.label.toLowerCase()))
    .join(", ")} and more`;
}

/**
 * A cabinet's storage ({ used, trash, quota } in bytes) for meters: how full
 * it is, what's left, and a label like "1.2 GB of 5 GB". Files in the Trash count.
 */
export function storageUse(storage) {
  const used = Number(storage && storage.used) || 0;
  const quota = storage && storage.quota ? Number(storage.quota) : null;
  const pct = quota ? Math.min(100, Math.round((used / quota) * 100)) : null;
  let tone = "none";
  if (pct !== null) tone = pct >= 95 ? "full" : pct >= 80 ? "warn" : "ok";
  return {
    used,
    quota,
    pct,
    tone,
    trash: Number(storage && storage.trash) || 0,
    left: quota ? Math.max(0, quota - used) : null,
    label: quota ? `${formatSize(used)} of ${formatSize(quota)}` : `${formatSize(used)} used`,
  };
}

export function formatDate(value) {
  return value ? moment(value).format("MMM D, YYYY") : "";
}

export function formatDateTime(value) {
  return value ? moment(value).format("MMM D, YYYY h:mm A") : "";
}

export function fromNow(value) {
  return value ? moment(value).fromNow() : "";
}

/**
 * "today at 5:00 PM", "tomorrow at 8:00 AM", "on Oct 15 at 5:00 PM" (the year
 * only when it isn't this year). short: "today, 5:00 PM", "Oct 15, 5:00 PM".
 */
export function whenText(value, { short = false, now = Date.now() } = {}) {
  if (!value) return "";
  const m = moment(value);
  const time = m.format("h:mm A");
  const days = m.clone().startOf("day").diff(moment(now).startOf("day"), "days");
  const day = { "-1": "yesterday", 0: "today", 1: "tomorrow" }[days];
  if (day) return short ? `${day}, ${time}` : `${day} at ${time}`;
  const date = m.format(m.year() === moment(now).year() ? "MMM D" : "MMM D, YYYY");
  return short ? `${date}, ${time}` : `on ${date} at ${time}`;
}

/**
 * A cabinet's uploads, for chips and banners. uploads: the server's
 * { open, mode, state, opens_at, closes_at, note } (missing: open, as before
 * the feature). state: open | closing (closes by itself) | closed (by hand) |
 * not_yet (opens by itself) | ended (its closing time passed).
 * now: the server's time in ms, for "in 2 hours".
 */
export function uploadsStatus(uploads, now = Date.now()) {
  const u = uploads || {};
  const until = (value) => moment(value).from(moment(now));
  switch (u.state) {
    case "closed":
      return {
        open: false,
        tone: "closed",
        icon: "bi-lock",
        short: "Uploads closed",
        title: "Uploads are closed",
        detail: "Nobody can upload to this cabinet until uploads are opened again.",
      };
    case "not_yet":
      return {
        open: false,
        tone: "later",
        icon: "bi-calendar-event",
        short: `Opens ${whenText(u.opens_at, { short: true, now })}`,
        title: `Uploads open ${whenText(u.opens_at, { now })}`,
        detail:
          `That's ${until(u.opens_at)}. Nobody can upload until then.` +
          (u.closes_at ? ` They close ${whenText(u.closes_at, { now })}.` : ""),
      };
    case "ended":
      return {
        open: false,
        tone: "closed",
        icon: "bi-lock",
        short: "Uploads closed",
        title: `Uploads closed ${whenText(u.closes_at, { now })}`,
        detail: "Nobody can upload to this cabinet until uploads are opened again.",
      };
    case "closing":
      return {
        open: true,
        tone: "closing",
        icon: "bi-hourglass-split",
        short: `Closes ${whenText(u.closes_at, { short: true, now })}`,
        title: `Uploads close ${whenText(u.closes_at, { now })}`,
        detail: `That's ${until(u.closes_at)}. Files still uploading then aren't saved, so upload before that.`,
      };
    default:
      return { open: true, tone: "open", icon: "bi-unlock", short: "Uploads open", title: "Uploads are open", detail: "" };
  }
}

/** The best message in an axios error (Blob bodies included). */
export async function errorMessage(err, fallback = "Something went wrong. Please try again.") {
  let data = err && err.response && err.response.data;
  if (data instanceof Blob) {
    try {
      data = JSON.parse(await data.text());
    } catch (e) {
      data = null;
    }
  }
  if (data && data.errors) {
    const first = Object.values(data.errors)[0];
    if (Array.isArray(first) && first.length) return first[0];
  }
  if (data && data.message) return data.message;
  if (err && err.response && err.response.status === 413) return "That file is too big for the server.";
  if (err && !err.response) return "Can't reach the server. Check your connection and try again.";
  return fallback;
}

/** Save a Blob as a file. */
export function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export async function downloadFile(file) {
  const res = await axios.get(`api/archives/files/${file.id}/download`, { responseType: "blob" });
  saveBlob(res.data, file.name);
}

/** Google apps that edit Word, Excel and PowerPoint files kept in Google Drive. */
export const GOOGLE_APPS = { document: "Google Docs", spreadsheet: "Google Sheets", presentation: "Google Slides" };

/** "Edit in Google Docs", "Open in Google Sheets"…, or "" when the file can't open in Google. */
export function googleAction(file) {
  if (!file || file.type === "folder" || !file.in_drive || !GOOGLE_APPS[file.google_kind]) return "";
  return `${file.can_edit ? "Edit" : "Open"} in ${GOOGLE_APPS[file.google_kind]}`;
}

/** A CCMIS link to a file or folder. It opens only for people with access to the cabinet. */
export function archiveLink(item, cabinetId) {
  const origin = window.location.origin;
  return item.type === "folder"
    ? `${origin}/archives/${cabinetId}?folder=${item.id}`
    : `${origin}/archives/open/${item.id}`;
}

export async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    // Older browsers, or a page without clipboard permission.
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    let copied = false;
    try {
      copied = document.execCommand("copy");
    } catch (err) {
      copied = false;
    }
    area.remove();
    return copied;
  }
}

/**
 * Open a file in Google Docs, Sheets or Slides. CCMIS first shares it with the
 * person's own Google account (their CCMIS email). The tab is opened before
 * the request so pop-up blockers allow it.
 */
export async function openInGoogle(file) {
  const tab = window.open("", "_blank");
  if (tab) {
    tab.opener = null;
    tab.document.title = "Opening…";
    tab.document.body.innerHTML = '<p style="font:15px system-ui,sans-serif;margin:32px;color:#4b5563">Opening in Google…</p>';
  }
  try {
    const { data } = await axios.post(`api/archives/files/${file.id}/google`);
    if (tab) tab.location.href = data.url;
    else window.location.assign(data.url);
    return data;
  } catch (err) {
    if (tab) tab.close();
    throw err;
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Upload one file in chunks (so PHP's upload limit doesn't matter). Reports
 * progress as 0..1, retries a failed chunk a few times, and resumes where the
 * server says it is. `job.cancelled = true` stops it after the current chunk.
 * Resolves with the server's { message, file }.
 */
export async function uploadInChunks(job, { cabinetId, folderId, onProgress }) {
  const file = job.file;
  const start = await axios.post("api/archives/uploads", {
    cabinet_id: cabinetId,
    folder_id: folderId || null,
    name: file.name,
    size: file.size,
    mime: file.type || null,
  });
  const { id, chunk_size: chunkSize } = start.data;
  job.uploadId = id;

  try {
    let offset = 0;
    while (offset < file.size) {
      if (job.cancelled) throw Object.assign(new Error("Cancelled"), { cancelled: true });
      const form = new FormData();
      form.append("offset", offset);
      form.append("chunk", file.slice(offset, offset + chunkSize), "chunk");

      for (let attempt = 1; ; attempt++) {
        try {
          const res = await axios.post(`api/archives/uploads/${id}/chunk`, form);
          offset = res.data.received;
          break;
        } catch (err) {
          const status = err.response && err.response.status;
          const received = err.response && err.response.data && err.response.data.received;
          if (status === 409 && typeof received === "number") {
            offset = received;
            break;
          }
          const retryable = !status || status >= 500 || status === 429;
          if (!retryable || attempt >= 3) throw err;
          await sleep(1000 * attempt);
        }
      }
      onProgress(Math.min(1, offset / file.size));
    }

    const done = await axios.post(`api/archives/uploads/${id}/complete`);
    return done.data;
  } catch (err) {
    axios.delete(`api/archives/uploads/${id}`).catch(() => {});
    throw err;
  }
}
